import { useState, useEffect, useCallback } from "react";
import { Plus, Search, AlertTriangle, Edit2, Trash2 } from "lucide-react";
import api from "../../services/api";
import ClaimForm from "./ClaimForm";
import { useAuth } from "../../context/AuthContext";

var STATUS_CLS = { aberto: "bg-blue-100 text-blue-700", em_analise: "bg-amber-100 text-amber-700", aprovado: "bg-emerald-100 text-emerald-700", negado: "bg-red-100 text-red-700", concluido: "bg-gray-100 text-gray-600" };
var STATUS_LABELS = { aberto: "Aberto", em_analise: "Em Analise", aprovado: "Aprovado", negado: "Negado", concluido: "Concluido" };
var TYPE_LABELS = { colisao: "Colisao", roubo: "Roubo", incendio: "Incendio", alagamento: "Alagamento", terceiros: "Terceiros", outros: "Outros" };

function fmtCurrency(v) {
  if (!v) return "R$ 0";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

function ClaimRow({ claim, onEdit, onDelete, isAdmin }) {
  var statusCls = STATUS_CLS[claim.status] || "bg-gray-100 text-gray-500";
  var statusLbl = STATUS_LABELS[claim.status] || claim.status;
  var typeLbl = TYPE_LABELS[claim.claim_type] || claim.claim_type;

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50">
      <td className="py-3 px-4 text-sm font-mono text-gray-500">{claim.policy_number}</td>
      <td className="py-3 px-4 text-sm text-gray-700">{claim.client_name}</td>
      <td className="py-3 px-4 text-sm text-gray-600">{claim.insurer_name}</td>
      <td className="py-3 px-4"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{typeLbl}</span></td>
      <td className="py-3 px-4 text-sm text-gray-700">{fmtCurrency(claim.damage_value)}</td>
      <td className="py-3 px-4 text-sm font-medium text-emerald-600">{claim.approved_value ? fmtCurrency(claim.approved_value) : "—"}</td>
      <td className="py-3 px-4"><span className={"text-xs font-medium px-2 py-0.5 rounded-full " + statusCls}>{statusLbl}</span></td>
      <td className="py-3 px-4">
        <div className="flex gap-1">
          {isAdmin && <button onClick={function() { onEdit(claim); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-[#3B82F6]" data-testid={"edit-claim-" + claim.id}><Edit2 className="w-4 h-4" /></button>}
          {isAdmin && <button onClick={function() { onDelete(claim.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={"delete-claim-" + claim.id}><Trash2 className="w-4 h-4" /></button>}
        </div>
      </td>
    </tr>
  );
}

export default function ClaimsTab() {
  var auth = useAuth();
  var isAdmin = auth.user && (auth.user.role === "admin" || auth.user.role === "team_leader");
  var claimsState = useState([]);
  var claims = claimsState[0];
  var setClaims = claimsState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var showFormState = useState(false);
  var showForm = showFormState[0];
  var setShowForm = showFormState[1];
  var editingState = useState(null);
  var editing = editingState[0];
  var setEditing = editingState[1];
  var searchState = useState("");
  var search = searchState[0];
  var setSearch = searchState[1];

  var fetchClaims = useCallback(function() {
    setLoading(true);
    var qs = "?limit=100";
    if (search) qs += "&search=" + encodeURIComponent(search);
    api.get("/claims" + qs).then(function(res) { setClaims(res.data.claims || []); }).finally(function() { setLoading(false); });
  }, [search]);

  useEffect(function() { fetchClaims(); }, [fetchClaims]);

  function handleSave() { setShowForm(false); setEditing(null); fetchClaims(); }
  function handleEdit(c) { setEditing(c); setShowForm(true); }
  function handleDelete(id) {
    if (!window.confirm("Excluir este sinistro?")) return;
    api.delete("/claims/" + id).then(function() { fetchClaims(); });
  }

  if (showForm) {
    return <ClaimForm claim={editing} onSave={handleSave} onCancel={function() { setShowForm(false); setEditing(null); }} />;
  }

  return (
    <div data-testid="claims-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="Buscar sinistro..." className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56" data-testid="search-claims" />
        </div>
        <button onClick={function() { setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg text-sm font-medium hover:bg-blue-600" data-testid="new-claim-btn">
          <Plus className="w-4 h-4" />Novo Sinistro
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      ) : claims.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Nenhum sinistro registrado</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Apolice</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Seguradora</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Dano</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Aprovado</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {claims.map(function(c) { return <ClaimRow key={c.id} claim={c} onEdit={handleEdit} onDelete={handleDelete} isAdmin={isAdmin} />; })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
